import { type Mapper } from '@coris-ts/repository/mapper/mapper'
import { type MediaPageEntity, type MediaTotalsEntity } from '../../data/entities/media.entity'
import { type SongData, type SongsResponse, type SongsSummary, type SongsTimeSeriesData } from '../models/songs'

const SONG_COLORS = [
  '#3B82F6',
  '#F59E0B',
  '#10B981',
  '#EF4444',
  '#8B5CF6',
  '#EC4899',
  '#06B6D4',
  '#84CC16',
  '#F97316',
  '#6366F1'
]

export class MediaPageToSongsMapper implements Mapper<MediaPageEntity, { songs: SongData[]; totalSongs: number }> {
  public map(from: MediaPageEntity): { songs: SongData[]; totalSongs: number } {
    return {
      songs: from.items.map((item, index) => ({
        id: `song-${index}`,
        name: item.title,
        imageUrl: item.icon_url,
        color: SONG_COLORS[index % SONG_COLORS.length],
        timesPlayed: item.times_played.current,
        timesPlayedChange: item.times_played.diff,
        uniqueListeners: item.unique_listeners.current,
        uniqueListenersChange: item.unique_listeners.diff
      })),
      totalSongs: from.total
    }
  }
}

export class MediaTotalsToSongsSummaryMapper
  implements Mapper<MediaTotalsEntity, { summary: SongsSummary; timeSeries: SongsTimeSeriesData[] }>
{
  public map(from: MediaTotalsEntity): { summary: SongsSummary; timeSeries: SongsTimeSeriesData[] } {
    return {
      summary: {
        totalTimesPlayed: from.times_played.current,
        totalTimesPlayedChange: from.times_played.diff,
        uniqueListeners: from.unique_listeners.current,
        uniqueListenersChange: from.unique_listeners.diff
      },
      timeSeries: from.periods.map((period) => ({
        date: period.date,
        timesPlayed: period.times_played,
        uniqueListeners: period.unique_listeners
      }))
    }
  }
}

export class CombinedMediaToSongsResponseMapper
  implements Mapper<{ page: MediaPageEntity; totals: MediaTotalsEntity }, SongsResponse>
{
  private readonly pageMapper = new MediaPageToSongsMapper()
  private readonly totalsMapper = new MediaTotalsToSongsSummaryMapper()

  public map(from: { page: MediaPageEntity; totals: MediaTotalsEntity }): SongsResponse {
    const { songs, totalSongs } = this.pageMapper.map(from.page)
    const { summary, timeSeries } = this.totalsMapper.map(from.totals)

    return {
      summary,
      timeSeries,
      songs,
      totalSongs
    }
  }
}
